import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Avatar,
    Rating,
    Tabs,
    Tab,
    Select,
    MenuItem,
    Menu,
    Alert,
    InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import ClearIcon from "@mui/icons-material/Clear";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import LoadingSpinner from "../LoadingContext";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";
import "./Trucks.css";

// Helper for Avatar initials
const getInitials = (name) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

// TabPanel component
function CustomTabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// Main Component
function Trucks() {
    const [trucks, setTrucks] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [error, setError] = useState(null);

    const [searchTextValue, setSearchTextValue] = useState("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(0);
    const [trucksPerPage, setTrucksPerPage] = useState(10);

    const [tabValue, setTabValue] = useState(0);
    const [statusFilter, setStatusFilter] = useState("all");

    const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const [selectedTruckId, setSelectedTruckId] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const openFilterMenu = Boolean(filterMenuAnchorEl);
    const openUserMenu = Boolean(userMenuAnchorEl);

    const navigate = useNavigate();

    // Fetch trucks
    const fetchTrucks = useCallback(async () => {
        setIsLoadingData(true);
        setError(null);

        try {
            const response = await getData(
                API_ENDPOINTS.getTrucks,
                appliedSearchTerm ? { search: appliedSearchTerm } : {}
            );
            if (response && Array.isArray(response.result)) {
                const mappedTrucks = response.result.map((truck) => ({
                    ...truck,
                    vehicleId: truck.truck_id,
                    truckNumber: truck.truck_no,
                    capacity: `${truck.capacity_inkgs} kgs / ${truck.capacity_inspace} space`,
                    status: truck.tagged_to_trip ? "On-Trip" : "Active",
                    currentLocation: "N/A",
                    rating: truck.average_rating,
                    driverName: truck.driver_id
                        ? `Driver ${truck.driver_id}`
                        : "No Driver",
                }));
                setTrucks(mappedTrucks);
            } else {
                setError("Received unexpected data format from the server.");
                setTrucks([]);
            }
        } catch (err) {
            setError("Failed to load trucks. Please try again later.");
        } finally {
            setIsLoadingData(false);
        }
    }, [appliedSearchTerm]);

    useEffect(() => {
        fetchTrucks();
    }, [fetchTrucks]);

    // Filter trucks based on status
    const filteredTrucks = useMemo(() => {
        if (statusFilter === "all") return trucks;
        return trucks.filter(
            (truck) =>
                truck.status.toLowerCase().replace(/\s+/g, "-") === statusFilter
        );
    }, [trucks, statusFilter]);

    // Paginate trucks
    const paginatedTrucks = useMemo(() => {
        const startIndex = currentPage * trucksPerPage;
        return filteredTrucks.slice(startIndex, startIndex + trucksPerPage);
    }, [filteredTrucks, currentPage, trucksPerPage]);

    const totalPages = Math.ceil(filteredTrucks.length / trucksPerPage);

    // Handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleTrucksPerPageChange = (event) => {
        setTrucksPerPage(Number(event.target.value));
        setCurrentPage(0);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleFilterButtonClick = (event) => {
        setFilterMenuAnchorEl(event.currentTarget);
    };

    const handleFilterMenuClose = () => setFilterMenuAnchorEl(null);

    const handleStatusFilterChange = (filter) => {
        setStatusFilter(filter);
        setCurrentPage(0);
        handleFilterMenuClose();
    };

    const handleMoreOptionsClick = (event, truckId, truckOwnerId) => {
        setUserMenuAnchorEl(event.currentTarget);
        setSelectedTruckId(truckId);
        setSelectedUserId(truckOwnerId);
    };

    const handleSearchTextChange = (e) => setSearchTextValue(e.target.value);

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") {
            setAppliedSearchTerm(searchTextValue);
            setCurrentPage(0);
        }
    };

    const handleClearSearch = () => {
        setSearchTextValue("");
        setAppliedSearchTerm("");
        setCurrentPage(0);
    };

    const renderStatus = (status) => (
        <span
            className={`status-indicator status-${status
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
        >
            <span className="status-dot"></span> {status || "Unknown"}
        </span>
    );

    const renderRating = (rating) => (
        <Rating
            name="read-only"
            value={rating || 0}
            precision={0.5}
            readOnly
            size="small"
        />
    );

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
        setSelectedUserId(null);
    };

    const handleViewProfile = useCallback(
        (vehicleId) => {
            const truckId = encryptId(vehicleId);
            navigate(`/app/truck-details/${truckId}`);
            handleUserMenuClose();
        },
        [navigate]
    );

    return (
        <Box className="user-panel-container">
            <LoadingSpinner isLoading={isLoadingData} />

            <Typography
                variant="h5"
                component="h1"
                gutterBottom
                className="main-title"
            >
                Trucks
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="All Trucks" />
                </Tabs>
            </Box>

            <CustomTabPanel value={tabValue} index={0}>
                <Row className="control-bar mb-4 align-items-center">
                    <Col xs={12} md={7} lg={8} className="search-col">
                        <TextField
                            fullWidth
                            placeholder="Search by truck name & truck number"
                            variant="outlined"
                            size="small"
                            value={searchTextValue}
                            onChange={handleSearchTextChange}
                            onKeyDown={handleSearchKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTextValue && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={handleClearSearch}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Col>
                    <Col
                        xs={12}
                        md={5}
                        lg={4}
                        className="d-flex justify-content-end align-items-center mt-2 mt-md-0"
                    >
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={handleFilterButtonClick}
                            aria-controls={
                                openFilterMenu ? "filter-menu" : undefined
                            }
                            aria-haspopup="true"
                            aria-expanded={openFilterMenu ? "true" : undefined}
                            className="filter-button"
                        >
                            Filter
                        </Button>
                        <Menu
                            anchorEl={filterMenuAnchorEl}
                            open={openFilterMenu}
                            onClose={handleFilterMenuClose}
                        >
                            <MenuItem
                                onClick={() => handleStatusFilterChange("all")}
                            >
                                All
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleStatusFilterChange("active")}
                            >
                                Active
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    handleStatusFilterChange("inactive")
                                }
                            >
                                Inactive
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    handleStatusFilterChange("on-trip")
                                }
                            >
                                On-Trip
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    handleStatusFilterChange("maintenance")
                                }
                            >
                                Maintenance
                            </MenuItem>
                        </Menu>
                    </Col>
                </Row>

                {error && !isLoadingData ? (
                    <Alert severity="error">{error}</Alert>
                ) : paginatedTrucks.length === 0 && !isLoadingData ? (
                    <Typography>No trucks found.</Typography>
                ) : (
                    <>
                        <div className="list-view-container">
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>Truck Name</th>
                                        <th>Truck Number</th>
                                        <th>Capacity</th>
                                        <th>Status</th>
                                        <th>Rating</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTrucks.map((truck) => (
                                        <tr key={truck.truck_id}>
                                            <td>
                                                <div className="truck-info-cell d-flex align-items-center gy-5 gx-5">
                                                    {truck.truck_pic ? (
                                                        <Avatar
                                                            src={truck.truck_pic}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                            }}
                                                            className="gx-3"
                                                        />
                                                    ) : (
                                                        <Avatar
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                            }}
                                                            className="gx-3"
                                                        >
                                                            {getInitials(
                                                                truck.truck_name
                                                            )}
                                                        </Avatar>
                                                    )}
                                                    <div className="mx-2 d-flex flex-column">
                                                        <span>
                                                            {truck.truck_name}
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: "14px",
                                                                fontWeight: "300",
                                                                color: "#aeaeae",
                                                            }}
                                                        >
                                                            Owner:{" "}
                                                            {truck.truck_owner_name
                                                                ? truck.truck_owner_name
                                                                : "Unknown Owner"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{truck.truck_no}</td>
                                            <td>{truck.capacity}</td>
                                            <td>{renderStatus(truck.status)}</td>
                                            <td>{renderRating(truck.rating)}</td>
                                            <td className="text-end">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) =>
                                                        handleMoreOptionsClick(
                                                            e,
                                                            truck.truck_id,
                                                            truck.truck_owner_id
                                                        )
                                                    }
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={userMenuAnchorEl}
                                                    open={
                                                        openUserMenu &&
                                                        selectedUserId ===
                                                        truck.truck_owner_id
                                                    }
                                                    onClose={handleUserMenuClose}
                                                    PaperProps={{
                                                        sx: {
                                                            boxShadow: "none",
                                                            border: "1px solid #ddd",
                                                        },
                                                    }}
                                                >
                                                    <MenuItem
                                                        onClick={() =>
                                                            handleViewProfile(
                                                                selectedTruckId
                                                            )
                                                        }
                                                    >
                                                        View Profile
                                                    </MenuItem>
                                                </Menu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {!isLoadingData && filteredTrucks.length > 0 && (
                            <Box
                                className="pagination-controls"
                                display="flex"
                                justifyContent="end"
                                alignItems="center"
                                mt={2}
                            >
                                <div
                                    className="items-per-page"
                                    style={{ display: "flex", alignItems: "center" }}
                                >
                                    <Select
                                        value={trucksPerPage}
                                        onChange={handleTrucksPerPageChange}
                                        size="small"
                                    >
                                        {[5, 10, 25, 50].map((opt) => (
                                            <MenuItem key={opt} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                        per page
                                    </Typography>
                                </div>

                                <Typography variant="body2" sx={{ mx: 2 }}>
                                    {currentPage + 1} of {totalPages} pages
                                </Typography>

                                <IconButton
                                    onClick={() =>
                                        handlePageChange(Math.max(currentPage - 1, 0))
                                    }
                                    disabled={currentPage === 0}
                                >
                                    <KeyboardArrowLeft />
                                </IconButton>
                                <IconButton
                                    onClick={() =>
                                        handlePageChange(
                                            Math.min(currentPage + 1, totalPages - 1)
                                        )
                                    }
                                    disabled={currentPage >= totalPages - 1}
                                >
                                    <KeyboardArrowRight />
                                </IconButton>
                            </Box>
                        )}
                    </>
                )}
            </CustomTabPanel>
        </Box>
    );
}

export default Trucks;
